import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { getUser } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { MapPin, Users, StickyNote, Loader2, Sparkles } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import RoutePreviewMap from "@/components/RoutePreviewMap";

export default function BookingPage({ source = "website" }) {
  const nav = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const [services, setServices] = useState([]);
  const [payMethods, setPayMethods] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [recent, setRecent] = useState({ pickups: [], dropoffs: [] });
  const [autoDistance, setAutoDistance] = useState(false);
  const [coords, setCoords] = useState({ pickup: null, dropoff: null });
  const [loading, setLoading] = useState(false);
  const [pattern, setPattern] = useState(null);
  const [credits, setCredits] = useState(0);
  const [form, setForm] = useState({
    rider_name: "", rider_mobile: "", rider_email: "",
    pickup_location: prefill.pickup_location || "",
    dropoff_location: prefill.dropoff_location || "",
    pickup_lat: null, pickup_lng: null, dropoff_lat: null, dropoff_lng: null,
    pickup_type: "asap", pickup_time: "",
    passenger_count: 1, notes: "",
    service_id: "", payment_method_id: "",
    estimated_distance_km: 10,
    source,
    promo_code: "",
    tip_percent: 0,
    custom_field_answers: {},
  });
  const [promoApplied, setPromoApplied] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get("/services"),
          api.get("/payment-methods/public"),
        ]);
        const activeServices = s.data.filter((x) => x.active);
        setServices(activeServices);
        setPayMethods(p.data);
        const dflt = p.data.find((m) => m.is_default) || p.data[0];
        const user = getUser();
        setForm((f) => ({
          ...f,
          service_id: activeServices[0]?.id || "",
          payment_method_id: dflt?.id || "",
          rider_name: user?.role === "rider" ? user.name : f.rider_name,
          rider_email: user?.role === "rider" ? user.email : f.rider_email,
          rider_mobile: user?.role === "rider" && user.mobile ? user.mobile : f.rider_mobile,
        }));
        if (user?.role === "rider") {
          try {
            const r = await api.get("/rider/me"); setSavedAddresses(r.data.saved_addresses || []);
            const ra = await api.get("/rider/recent-addresses"); setRecent(ra.data || { pickups: [], dropoffs: [] });
          } catch {}
        }
      } catch (e) { toast.error("Could not load form data"); }
    })();
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  // Haversine fallback + async OSRM routed distance
  const haversineKm = (a, b) => {
    const R = 6371, toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat))*Math.cos(toRad(b.lat))*Math.sin(dLng/2)**2;
    return Math.round(2 * R * Math.asin(Math.sqrt(s)) * 10) / 10;
  };
  const recalcDistance = async (pickup, dropoff) => {
    if (!pickup || !dropoff) return;
    // Optimistic: haversine first so the field responds instantly
    const fallback = haversineKm(pickup, dropoff);
    setForm((f) => ({ ...f, estimated_distance_km: fallback }));
    setAutoDistance(true);
    // Then try OSRM public routing for a true driving distance
    try {
      const url = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${dropoff.lng},${dropoff.lat}?overview=false`;
      const res = await fetch(url);
      const data = await res.json();
      const meters = data?.routes?.[0]?.distance;
      if (meters && meters > 0) {
        const km = Math.round((meters / 1000) * 10) / 10;
        setForm((f) => ({ ...f, estimated_distance_km: km }));
      }
    } catch { /* keep haversine */ }
  };
  const onPickupSelect = ({ lat, lng }) => {
    const next = { lat, lng };
    setForm((f) => ({ ...f, pickup_lat: lat, pickup_lng: lng }));
    setCoords((c) => { const nc = { ...c, pickup: next }; recalcDistance(nc.pickup, nc.dropoff); return nc; });
  };
  const onDropoffSelect = ({ lat, lng }) => {
    const next = { lat, lng };
    setForm((f) => ({ ...f, dropoff_lat: lat, dropoff_lng: lng }));
    setCoords((c) => { const nc = { ...c, dropoff: next }; recalcDistance(nc.pickup, nc.dropoff); return nc; });
  };

  // Load booking-time hint + current promo credits for signed-in riders
  useEffect(() => {
    const u = getUser();
    if (u?.role !== "rider") return;
    (async () => {
      try {
        const [p, r] = await Promise.all([
          api.get("/rider/booking-pattern").catch(() => ({ data: null })),
          api.get("/rider/referral").catch(() => ({ data: null })),
        ]);
        if (p?.data?.has_pattern) setPattern(p.data);
        if (r?.data) setCredits(r.data.credits_nzd || 0);
      } catch {}
    })();
  }, []);

  const svc = services.find((s) => s.id === form.service_id);
  const customFields = svc?.custom_fields || [];
  const baseFare = svc ? svc.base_fare + svc.per_km * Number(form.estimated_distance_km || 0) : 0;
  const [surge, setSurge] = useState(null);

  // Debounced surge preview when pickup coords change
  useEffect(() => {
    if (form.pickup_lat == null || form.pickup_lng == null) { setSurge(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/surge/preview?lat=${form.pickup_lat}&lng=${form.pickup_lng}`);
        setSurge(data.active ? data : null);
      } catch { setSurge(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.pickup_lat, form.pickup_lng]);

  const surgedFare = surge ? baseFare * surge.multiplier : baseFare;
  const preTipFare = (promoApplied ? promoApplied.new_fare : surgedFare);
  const tipAmount = preTipFare * (Number(form.tip_percent || 0) / 100);
  const fare = (preTipFare + tipAmount).toFixed(2);

  const applyPromo = async () => {
    if (!form.promo_code) return;
    try {
      const { data } = await api.post("/promos/apply", { code: form.promo_code, fare: baseFare });
      setPromoApplied(data);
      toast.success(`Applied — saved NZ$${data.discount.toFixed(2)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); setPromoApplied(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rider_name || !form.rider_mobile || !form.pickup_location || !form.dropoff_location) {
      toast.error("Please fill in all required fields"); return;
    }
    setLoading(true);
    try {
      const payload = { ...form, passenger_count: Number(form.passenger_count), estimated_distance_km: Number(form.estimated_distance_km), tip_percent: Number(form.tip_percent || 0), tip_split_driver_pct: Number(form.tip_split_driver_pct ?? 100), tip_split_dispatcher_pct: Number(form.tip_split_dispatcher_pct ?? 0), tip_split_support_pct: Number(form.tip_split_support_pct ?? 0) };
      if (!payload.rider_email) delete payload.rider_email;
      if (payload.pickup_type === "asap") payload.pickup_time = null;
      const { data } = await api.post("/bookings", payload);
      const tp = Number(form.tip_percent || 0);
      if (tp > 0) {
        toast.success(`Booking confirmed — Ref ${data.reference}. Your ${tp}% tip goes 100% to the driver 🎉`);
      } else {
        toast.success(`Booking confirmed — Ref ${data.reference}`);
      }
      // Pass the per-booking access token so the confirmation + tracking
      // pages can fetch details without leaking every reference publicly.
      nav(`/confirmation/${data.reference}?token=${encodeURIComponent(data.access_token || "")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <div className="text-caption mb-3">Booking · {source === "qr" ? "QR quick-book" : "Website"}</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-display leading-tight">Plan your ride.</h1>
        <p className="text-slate-400 mt-3 max-w-lg">Fill in the details — we'll confirm on-screen instantly and send a receipt to your email.</p>

        <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-5 glass-card rounded-2xl p-6 sm:p-8 gold-border fade-up">
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-5">
            <Field label="Full name *">
              <Input data-testid={TIDS.booking.riderName} value={form.rider_name} onChange={set("rider_name")} placeholder="Jane Wilson" className="bg-black/30 border-white/10 h-11" />
            </Field>
            <Field label="Mobile *">
              <Input data-testid={TIDS.booking.riderMobile} value={form.rider_mobile} onChange={set("rider_mobile")} placeholder="+64 22 000 0000" className="bg-black/30 border-white/10 h-11" />
            </Field>
          </div>
          <Field label="Email (for confirmation)" className="md:col-span-2">
            <Input data-testid={TIDS.booking.riderEmail} type="email" value={form.rider_email} onChange={set("rider_email")} placeholder="you@example.com" className="bg-black/30 border-white/10 h-11" />
          </Field>

          <Field label="Pickup *" icon={MapPin}>
            <AddressAutocomplete
              data-testid={TIDS.booking.pickup}
              value={form.pickup_location}
              onChange={(v) => setForm((f) => ({ ...f, pickup_location: v }))}
              onSelect={onPickupSelect}
              placeholder="123 Queen St, Auckland CBD"
            />
            {surge && (
              <div className="mt-3 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 flex items-center gap-3" data-testid="surge-banner">
                <div className="w-9 h-9 rounded-full bg-amber-400/20 grid place-items-center text-amber-400 font-bold font-display text-sm">×{surge.multiplier}</div>
                <div className="flex-1">
                  <div className="text-sm text-amber-200 font-semibold">Prices are {surge.multiplier}× right now due to demand</div>
                  <div className="text-[11px] text-amber-300/70 mt-0.5">Zone: {surge.name} · Surge applies at pickup time</div>
                </div>
              </div>
            )}
            {(savedAddresses.length > 0 || recent.pickups.length > 0) && (
              <div className="mt-2 flex flex-wrap gap-2">
                {savedAddresses.map((a) => (
                  <button type="button" key={a.id} onClick={() => setForm({ ...form, pickup_location: a.address })} className="text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full border border-amber-400/40 text-amber-400 hover:bg-amber-400/10" data-testid={`saved-addr-${a.label}`}>★ {a.label}</button>
                ))}
                {recent.pickups.map((addr, i) => (
                  <button type="button" key={`rp-${i}`} onClick={() => setForm({ ...form, pickup_location: addr })} title={addr} className="text-xs px-3 py-1 rounded-full border border-white/10 text-slate-300 hover:border-amber-400/40 hover:text-amber-400 max-w-[180px] truncate" data-testid={`recent-pickup-${i}`}>↻ {addr.split(",")[0]}</button>
                ))}
              </div>
            )}
          </Field>
          <Field label="Drop-off *" icon={MapPin}>
            <AddressAutocomplete
              data-testid={TIDS.booking.dropoff}
              value={form.dropoff_location}
              onChange={(v) => setForm((f) => ({ ...f, dropoff_location: v }))}
              onSelect={onDropoffSelect}
              placeholder="Auckland Airport, Mangere"
            />
            {recent.dropoffs.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {recent.dropoffs.map((addr, i) => (
                  <button type="button" key={`rd-${i}`} onClick={() => setForm({ ...form, dropoff_location: addr })} title={addr} className="text-xs px-3 py-1 rounded-full border border-white/10 text-slate-300 hover:border-amber-400/40 hover:text-amber-400 max-w-[180px] truncate" data-testid={`recent-dropoff-${i}`}>↻ {addr.split(",")[0]}</button>
                ))}
              </div>
            )}
          </Field>

          <Field label="When" className="md:col-span-2">
            <RadioGroup value={form.pickup_type} onValueChange={set("pickup_type")} className="flex gap-3" data-testid={TIDS.booking.pickupType}>
              <RadioOption value="asap" label="ASAP" active={form.pickup_type === "asap"} />
              <RadioOption value="scheduled" label="Scheduled" active={form.pickup_type === "scheduled"} />
            </RadioGroup>
            {form.pickup_type === "scheduled" && (
              <Input data-testid={TIDS.booking.pickupTime} type="datetime-local" value={form.pickup_time} onChange={set("pickup_time")} className="bg-black/30 border-white/10 h-11 mt-3" />
            )}
          </Field>

          <Field label="Passengers" icon={Users}>
            <Input data-testid={TIDS.booking.passengers} type="number" min="1" max="8" value={form.passenger_count} onChange={set("passenger_count")} className="bg-black/30 border-white/10 h-11" />
          </Field>
          <Field label={autoDistance ? "Est. distance (km) — auto from map" : "Est. distance (km)"}>
            <Input data-testid={TIDS.booking.distance} type="number" min="1" step="0.5" value={form.estimated_distance_km} onChange={(e) => { setAutoDistance(false); set("estimated_distance_km")(e); }} className={`bg-black/30 border-white/10 h-11 ${autoDistance ? "text-amber-400 font-mono" : ""}`} />
          </Field>

          {coords.pickup && coords.dropoff && (
            <div className="md:col-span-2" data-testid="route-preview-wrap">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Route preview</div>
              <RoutePreviewMap pickup={coords.pickup} dropoff={coords.dropoff} />
            </div>
          )}

          <Field label="Service">
            <Select value={form.service_id} onValueChange={set("service_id")}>
              <SelectTrigger data-testid={TIDS.booking.service} className="bg-black/30 border-white/10 h-11"><SelectValue placeholder="Select service" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 text-white">
                {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — NZ${s.base_fare.toFixed(2)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Payment method">
            <Select value={form.payment_method_id} onValueChange={set("payment_method_id")}>
              <SelectTrigger data-testid={TIDS.booking.payment} className="bg-black/30 border-white/10 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 text-white">
                {payMethods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Promo code" className="md:col-span-2">
            <div className="flex gap-2">
              <Input data-testid="booking-promo" value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })} placeholder="WELCOME10" className="bg-black/30 border-white/10 h-11 font-mono uppercase" />
              <Button type="button" onClick={applyPromo} variant="outline" className="bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10 h-11">Apply</Button>
            </div>
            {promoApplied && <div className="text-xs text-emerald-400 mt-2 font-mono">✓ {promoApplied.code} — saved NZ$ {promoApplied.discount.toFixed(2)}</div>}
          </Field>

          <Field label="Notes for driver" icon={StickyNote} className="md:col-span-2">
            <Textarea data-testid={TIDS.booking.notes} value={form.notes} onChange={set("notes")} rows={3} placeholder="Gate code, luggage, child seat…" className="bg-black/30 border-white/10" />
          </Field>

          <Field label="Tip for your driver (100% goes to them)" className="md:col-span-2">
            <div className="flex flex-wrap gap-2" data-testid="booking-tip-group">
              {[0, 5, 10, 15, 20].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, tip_percent: p, tip_split_driver_pct: 100, tip_split_dispatcher_pct: 0, tip_split_support_pct: 0 }))}
                  data-testid={`tip-${p}`}
                  className={`px-4 py-2 rounded-full border text-sm font-mono transition-all ${
                    Number(form.tip_percent || 0) === p
                      ? "border-red-400/60 bg-red-500/20 text-red-200 scale-105"
                      : "border-white/10 text-slate-300 hover:border-red-400/40 hover:text-red-300"
                  }`}
                >
                  {p === 0 ? "No tip" : `+${p}%`}
                </button>
              ))}
              {Number(form.tip_percent || 0) > 0 && (
                <span className="text-xs text-emerald-300 font-mono self-center ml-1" data-testid="tip-preview">
                  +NZ$ {tipAmount.toFixed(2)}
                </span>
              )}
            </div>
            {Number(form.tip_percent || 0) >= 10 && (
              <TipSplitEditor form={form} setForm={setForm} tipAmount={tipAmount} />
            )}
          </Field>

          {customFields.length > 0 && (
            <div className="md:col-span-2 space-y-3 pt-3 border-t border-white/5" data-testid="custom-fields-section">
              <div className="text-xs uppercase tracking-widest text-amber-400 font-mono">Extra details for {svc.name}</div>
              {customFields.map((cf) => (
                <div key={cf.key} className="grid gap-1">
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">{cf.label}{cf.required ? " *" : ""}</label>
                  {cf.type === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={!!form.custom_field_answers?.[cf.key]} onChange={(e) => setForm({ ...form, custom_field_answers: { ...form.custom_field_answers, [cf.key]: e.target.checked } })} data-testid={`cf-${cf.key}`} className="accent-amber-400 w-4 h-4" />
                      Yes
                    </label>
                  ) : (
                    <Input type={cf.type === "number" ? "number" : "text"} required={cf.required} value={form.custom_field_answers?.[cf.key] || ""} onChange={(e) => setForm({ ...form, custom_field_answers: { ...form.custom_field_answers, [cf.key]: e.target.value } })} data-testid={`cf-${cf.key}`} className="bg-black/30 border-white/10 h-11" />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/5">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Estimated fare</div>
              <div className="font-display text-3xl font-bold text-amber-400">NZ$ {fare}</div>
            </div>
            <Button data-testid={TIDS.booking.submit} type="submit" disabled={loading} className="btn-gold rounded-full h-12 px-8">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm booking
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, icon: Icon, className = "", children }) => (
  <div className={className}>
    <Label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </Label>
    {children}
  </div>
);

const RadioOption = ({ value, label, active }) => (
  <label className={`flex-1 cursor-pointer rounded-lg border px-4 py-3 text-sm font-medium text-center transition-colors ${active ? "border-amber-400/60 bg-amber-400/10 text-amber-300" : "border-white/10 hover:border-white/25 text-slate-300"}`}>
    <RadioGroupItem value={value} className="sr-only" />
    {label}
  </label>
);

function TipSplitEditor({ form, setForm, tipAmount }) {
  const drv = Number(form.tip_split_driver_pct ?? 100);
  const disp = Number(form.tip_split_dispatcher_pct ?? 0);
  const supp = Number(form.tip_split_support_pct ?? 0);
  const total = drv + disp + supp;
  const valid = total === 100;

  const set = (k) => (val) => {
    let n = Math.max(0, Math.min(100, Number(val) || 0));
    setForm((f) => ({ ...f, [k]: n }));
  };
  const rebalance = () => {
    // Auto-fill driver share so the split sums to 100
    setForm((f) => ({ ...f, tip_split_driver_pct: Math.max(0, 100 - disp - supp) }));
  };
  return (
    <div className="mt-3 p-3 rounded-lg bg-red-500/5 border border-red-400/20" data-testid="tip-split-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-red-300">Share the love — tip split</div>
        <button type="button" onClick={rebalance} className="text-[10px] font-mono uppercase tracking-widest text-red-300 hover:text-red-200" data-testid="tip-split-rebalance">
          Balance to 100%
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <SplitRow label="Driver" value={drv} onChange={set("tip_split_driver_pct")} amount={tipAmount * drv / 100} testid="split-driver" />
        <SplitRow label="Dispatcher" value={disp} onChange={set("tip_split_dispatcher_pct")} amount={tipAmount * disp / 100} testid="split-dispatcher" />
        <SplitRow label="Support" value={supp} onChange={set("tip_split_support_pct")} amount={tipAmount * supp / 100} testid="split-support" />
      </div>
      <div className={`text-[11px] mt-2 font-mono ${valid ? "text-emerald-300" : "text-amber-300"}`} data-testid="split-total-hint">
        Total: {total}% {valid ? "✓" : "— must add up to 100%"}
      </div>
    </div>
  );
}
const SplitRow = ({ label, value, onChange, amount, testid }) => (
  <div className="bg-black/20 rounded-md border border-white/5 p-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">{label}</div>
    <div className="flex items-baseline gap-1 mt-1">
      <input
        type="number" min="0" max="100" value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent w-12 text-right font-mono font-bold text-red-200 text-lg focus:outline-none"
        data-testid={`${testid}-input`}
      />
      <span className="text-slate-500 font-mono text-sm">%</span>
    </div>
    <div className="text-[10px] text-emerald-300 font-mono mt-0.5">NZ$ {amount.toFixed(2)}</div>
  </div>
);
